/* ==========================================================
   HBCA 2.0 — MOBILE PRODUCT APP
   Gallery / Thumbnails / Sticky Purchase / Variant Selector
   ========================================================== */

(() => {
  'use strict';

  function initHBCAProductApp() {

    const root = document.querySelector(
      'product-info:has(.hbca-product-page)'
    );

    if (!root || root.dataset.hbcaAppInitialized === 'true') return;

    const section = root.querySelector('.hbca-product-page');
    if (!section) return;

    root.dataset.hbcaAppInitialized = 'true';

    const mobileQuery = window.matchMedia('(max-width: 749px)');

    /* ======================================================
       1. PRODUCT GALLERY + THUMBNAILS
       ====================================================== */

    const gallery = section.querySelector('.product__media-list');

    const media = gallery
      ? [...gallery.querySelectorAll('.product__media-item')]
      : [];

    if (gallery && media.length) {
      const wrap = section.querySelector('.product__media-wrapper');

      if (wrap && !wrap.querySelector('.hbca-app-thumbs')) {
        const counter = document.createElement('div');
        counter.className = 'hbca-app-gallery-status';
        counter.setAttribute('aria-live', 'polite');
        wrap.appendChild(counter);

        const thumbs = document.createElement('div');
        thumbs.className = 'hbca-app-thumbs';
        thumbs.setAttribute('aria-label', 'Product gallery thumbnails');

        let activeIndex = 0;

        function setActive(index, navigate = false) {
          activeIndex = Math.max(0, Math.min(media.length - 1, index));

          thumbs.querySelectorAll('.hbca-app-thumb').forEach((btn, i) => {
            btn.setAttribute('aria-current', String(i === activeIndex));
          });

          counter.textContent =
            `${String(activeIndex + 1).padStart(2, '0')} / ` +
            `${String(media.length).padStart(2, '0')}`;

          if (mobileQuery.matches) {
            media.forEach(item => {
              item.classList.remove('hbca-desktop-active');
            });

            if (navigate) {
              const item = media[activeIndex];

              gallery.scrollTo({
                left: item.offsetLeft - gallery.offsetLeft,
                behavior: 'smooth'
              });
            }
          } else {
            media.forEach((item, i) => {
              item.classList.toggle(
                'hbca-desktop-active',
                i === activeIndex
              );
            });

            // Desktop image visibility is controlled only by the active class.
            // Do not call Dawn setActiveMedia here: it can re-order media
            // and conflict with the one-image desktop presentation.

          }
        }

        media.forEach((item, i) => {
          const img = item.querySelector('img');
          if (!img) return;

          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'hbca-app-thumb';

          btn.setAttribute(
            'aria-label',
            `View image ${i + 1}`
          );

          btn.setAttribute(
            'aria-current',
            String(i === 0)
          );

          const thumb = document.createElement('img');

          thumb.src = img.getAttribute('src') || img.currentSrc || img.src;
          thumb.alt = img.alt || `Product image ${i + 1}`;
          thumb.loading = 'lazy';
          thumb.decoding = 'async';

          btn.appendChild(thumb);

          btn.addEventListener('click', () => {
            setActive(i, true);
          });

          thumbs.appendChild(btn);
        });

        wrap.appendChild(thumbs);

        let frame = null;

        gallery.addEventListener(
          'scroll',
          () => {
            if (!mobileQuery.matches) return;

            if (frame) cancelAnimationFrame(frame);

            frame = requestAnimationFrame(() => {
              const x = gallery.getBoundingClientRect().left;

              let nearest = 0;
              let distance = Infinity;

              media.forEach((item, i) => {
                const d = Math.abs(
                  item.getBoundingClientRect().left - x
                );

                if (d < distance) {
                  distance = d;
                  nearest = i;
                }
              });

              setActive(nearest);
            });
          },
          { passive: true }
        );

        mobileQuery.addEventListener('change', () => {
          setActive(activeIndex);
        });

        setActive(0);
      }
    }

    /* ======================================================
       2. EXISTING SHOPIFY PRODUCT FORM
       ====================================================== */

    const form = section.querySelector(
      'form[id^="product-form-"]'
    );

    const nativeButton = form?.querySelector(
      '[type="submit"]'
    );

    if (!form || !nativeButton) return;

    const originalQty = section.querySelector(
      '.product-form__quantity input.quantity__input'
    );

    /* ======================================================
       3. STICKY PURCHASE BAR
       ====================================================== */

    let bar = root.querySelector('.hbca-app-purchase');

    if (!bar) {
      bar = document.createElement('div');

      bar.className = 'hbca-app-purchase';

      bar.setAttribute(
        'aria-label',
        'Quick purchase'
      );

      bar.innerHTML = `
        <div class="hbca-app-purchase__qty">

          <button
            type="button"
            data-step="-1"
            aria-label="Decrease quantity"
          >−</button>

          <output>1</output>

          <button
            type="button"
            data-step="1"
            aria-label="Increase quantity"
          >+</button>

        </div>

        <button
          type="button"
          class="hbca-app-purchase__buy"
        >
          ADD TO BAG
        </button>
      `;

      root.appendChild(bar);
    }

    const output = bar.querySelector('output');

    const buy = bar.querySelector(
      '.hbca-app-purchase__buy'
    );

    function getQty() {
      return originalQty
        ? Number(originalQty.value) || 1
        : 1;
    }

    function updatePurchase() {
      if (!output || !buy) return;

      output.textContent = String(getQty());

      buy.disabled = nativeButton.disabled;

      const price = section.querySelector(
        '[id^="price-"] .price-item--sale:not(.price-item--last), ' +
        '[id^="price-"] .price-item--last, ' +
        '[id^="price-"] .price-item--regular'
      );

      const priceText = price
        ? price.textContent.trim()
        : '';

      buy.textContent =
        (nativeButton.disabled ? 'UNAVAILABLE' : 'ADD TO BAG') +
        (priceText ? ' — ' + priceText : '');
    }

    bar.querySelectorAll('[data-step]').forEach(btn => {
      btn.addEventListener('click', () => {
        if (!originalQty) return;

        const delta = Number(btn.dataset.step);
        const step = Number(originalQty.step) || 1;
        const min = Number(originalQty.min) || 1;

        const max = originalQty.max
          ? Number(originalQty.max)
          : Infinity;

        originalQty.value = Math.max(
          min,
          Math.min(max, getQty() + delta * step)
        );

        originalQty.dispatchEvent(
          new Event('change', { bubbles: true })
        );

        updatePurchase();
      });
    });

    buy?.addEventListener('click', () => {
      if (nativeButton.disabled) return;
      nativeButton.click();
    });

    originalQty?.addEventListener(
      'change',
      updatePurchase
    );

    root.addEventListener(
      'variant:change',
      updatePurchase
    );

    new MutationObserver(updatePurchase).observe(
      nativeButton,
      {
        attributes: true,
        attributeFilter: ['disabled']
      }
    );

    const priceWrap = section.querySelector('[id^="price-"]');

    if (priceWrap) {
      new MutationObserver(updatePurchase).observe(
        priceWrap,
        {
          childList: true,
          subtree: true
        }
      );
    }

    updatePurchase();

    /* ======================================================
       4. MOBILE VARIANT SELECTOR
       Move ORIGINAL selector — never clone inputs
       ====================================================== */

    const selector = section.querySelector(
      '.product-form__input--swatch'
    );

    if (selector) {
      const originalParent = selector.parentNode;

      const marker = document.createComment(
        'HBCA original variant selector position'
      );

      originalParent.insertBefore(marker, selector);

      function positionVariantSelector() {
        if (mobileQuery.matches) {
          if (selector.parentNode !== bar) {
            bar.insertBefore(
              selector,
              bar.firstChild
            );
          }

          selector.classList.add(
            'hbca-app-sticky-variant'
          );
        } else {
          if (marker.parentNode) {
            marker.parentNode.insertBefore(
              selector,
              marker.nextSibling
            );
          }

          selector.classList.remove(
            'hbca-app-sticky-variant'
          );
        }
      }

      mobileQuery.addEventListener?.(
        'change',
        positionVariantSelector
      );

      positionVariantSelector();
    }

    /* ======================================================
       5. PRODUCT ACCORDIONS
       ====================================================== */

    const accordions = section.querySelector(
      '.hbca-media-accordions'
    );

    const info = section.querySelector(
      '.hbca-product__info-container'
    );

    if (accordions && info) {
      const originalParent = accordions.parentNode;

      const marker = document.createComment(
        'HBCA original accordion position'
      );

      originalParent.insertBefore(
        marker,
        accordions
      );

      const anchor = info.querySelector(
        '.hbca-product__bottom'
      );

      function positionAccordions() {
        if (mobileQuery.matches) {
          if (anchor) {
            info.insertBefore(
              accordions,
              anchor
            );
          } else {
            info.appendChild(accordions);
          }
        } else if (marker.parentNode) {
          marker.parentNode.insertBefore(
            accordions,
            marker.nextSibling
          );
        }
      }

      mobileQuery.addEventListener?.(
        'change',
        positionAccordions
      );

      positionAccordions();
    }
  }

  /* ========================================================
     INITIALIZATION
     ======================================================== */

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initHBCAProductApp
    );
  } else {
    initHBCAProductApp();
  }
})();